/**
 * Phase 19.1 — Dashboard widget shown to Asst-Mgr portal users when the Owner has enabled it.
 * Lists portal users they're allowed to manage + a "Manage Access" deep-link that opens the
 * UserAccessControlDialog so they can toggle their whitelisted permission keys.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Shield, Users } from "lucide-react";
import UserAccessControlDialog from "./UserAccessControlDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ManagedPortalUsersWidget() {
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState({ enabled: false, widget_enabled: false, managed_users: [], allowed_keys: [] });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeUser, setActiveUser] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/portal/me/delegation`);
      setState(res.data || { enabled: false, widget_enabled: false, managed_users: [], allowed_keys: [] });
    } catch (_) {
      setState({ enabled: false, widget_enabled: false, managed_users: [], allowed_keys: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return null;
  if (!state.enabled || !state.widget_enabled) return null;
  if (!state.managed_users || state.managed_users.length === 0) {
    return (
      <Card data-testid="managed-users-widget-empty">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Shield className="h-4 w-4 text-amber-500" /> Portal Users I Manage
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground">
            The Owner has enabled delegation for you, but hasn't assigned any portal users yet.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card data-testid="managed-users-widget">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center justify-between gap-2 text-base">
            <div className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-amber-500" /> Portal Users I Manage
            </div>
            <Badge variant="outline" className="text-[10px]">
              {state.allowed_keys.length} key{state.allowed_keys.length === 1 ? "" : "s"} delegated
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1 max-h-72 overflow-y-auto">
            {state.managed_users.map((u) => (
              <div
                key={u.id}
                className="flex items-center justify-between gap-2 rounded-md border bg-card px-3 py-2"
                data-testid={`managed-user-${u.id}`}
              >
                <div className="min-w-0 flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{u.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant={u.is_active ? "outline" : "destructive"} className="text-[10px]">
                    {u.account_type === "client_portal" ? "Client" : "Employee"}
                  </Badge>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => { setActiveUser(u); setDialogOpen(true); }}
                    data-testid={`manage-access-widget-${u.id}`}
                  >
                    Manage Access
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
      <UserAccessControlDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        portalUser={activeUser}
        onSaved={load}
      />
    </>
  );
}
